import React, {
  forwardRef,
  type HTMLAttributes,
  type SelectHTMLAttributes,
  useState,
  useEffect,
} from "react";
import { cn } from "../../utils/cn";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: "primary" | "secondary" | "success" | "warning" | "outline";
  size?: "sm" | "md";
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { className, variant = "primary", size = "md", children, ...props },
    ref,
  ) => {
    const variantClasses = {
      primary: "badge-primary",
      secondary: "badge-secondary",
      success: "badge-success",
      warning: "badge-warning",
      outline: "border border-border bg-transparent text-text-secondary",
    };

    const sizeClasses = {
      sm: "px-2 py-0.5 text-xs",
      md: "px-2.5 py-1 text-xs",
    };

    return (
      <span
        ref={ref}
        className={cn(
          "badge",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = "Badge";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  options: { value: string; label: string; disabled?: boolean }[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, label, error, hint, placeholder, options, id, ...props },
    ref,
  ) => {
    const selectId =
      id || `select-${Math.random().toString(36).substring(2, 9)}`;
    const errorId = error ? `${selectId}-error` : undefined;
    const hintId = hint ? `${selectId}-hint` : undefined;
    const describedBy =
      [errorId, hintId].filter(Boolean).join(" ") || undefined;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={selectId} className="label-base">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              "input-base appearance-none pr-10",
              error && "input-error",
              className,
            )}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={describedBy}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted"
            aria-hidden="true"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
        </div>
        {error && (
          <p id={errorId} className="error-message" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-sm text-text-muted">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export interface CheckboxProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  label: string;
  description?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
}

export const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      checked,
      defaultChecked,
      onChange,
      disabled,
      id,
      name,
      required,
      ...props
    },
    ref,
  ) => {
    const checkboxId =
      id || `checkbox-${Math.random().toString(36).substring(2, 9)}`;
    const [isChecked, setIsChecked] = useState(defaultChecked || false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (checked !== undefined) setIsChecked(checked);
    }, [checked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;
      if (checked === undefined) setIsChecked(newChecked);
      onChange?.(newChecked);
    };

    return (
      <div
        ref={ref}
        className={cn("flex items-start gap-3", className)}
        {...props}
      >
        <label
          htmlFor={checkboxId}
          className="flex items-start gap-3 cursor-pointer"
        >
          <div className="relative flex items-center justify-center">
            <input
              type="checkbox"
              id={checkboxId}
              name={name}
              checked={isChecked}
              onChange={handleChange}
              disabled={disabled}
              required={required}
              className="absolute inset-0 opacity-0 cursor-pointer"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            <div
              className={cn(
                "w-5 h-5 rounded-sm border-2 transition-all duration-fast flex items-center justify-center",
                isChecked
                  ? "bg-accent border-accent text-accent-foreground"
                  : "border-border bg-surface hover:border-border-strong",
                disabled && "opacity-50 cursor-not-allowed",
                isFocused &&
                  !disabled &&
                  "ring-2 ring-accent ring-offset-2 ring-offset-background",
              )}
              aria-hidden="true"
            >
              {isChecked && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <div className="pt-0.5">
            <span
              className={cn(
                "font-medium text-text-primary",
                disabled && "text-text-muted",
              )}
            >
              {label}
              {required && (
                <span className="text-error ml-1" aria-hidden="true">
                  *
                </span>
              )}
            </span>
            {description && (
              <p className="body-sm text-text-muted mt-0.5">{description}</p>
            )}
          </div>
        </label>
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";
